<script lang="ts" setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { menuItems } from '@/data/menu';
import { ChevronDown } from 'lucide-vue-next';

const router = useRouter();
const isOpen = ref(false);
const expandedGroups = ref<Set<string>>(new Set());

const toggleMenu = () => {
	isOpen.value = !isOpen.value;
};

const toggleGroup = (label: string) => {
	if (expandedGroups.value.has(label)) {
		expandedGroups.value.delete(label);
	} else {
		expandedGroups.value.add(label);
	}
};

const navigate = (link: string) => {
	router.push(link);
	isOpen.value = false;
};
</script>

<template>
	<div>
		<!-- Hamburger Button -->
		<button
			@click="toggleMenu"
			class="flex items-center justify-center w-10 h-10 text-gray-400 hover:text-white focus:outline-hidden">
			<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
				<path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16m-7 6h7" />
			</svg>
		</button>

		<!-- Sheet menu -->
		<Sheet v-model:open="isOpen">
			<SheetContent side="right" class="w-[280px] p-0 overflow-y-auto">
				<SheetHeader class="px-4 pt-6 pb-3 border-b">
					<SheetTitle class="text-base font-medium">Menu</SheetTitle>
				</SheetHeader>
				<nav class="py-2">
					<template v-for="(item, index) in menuItems" :key="index">
						<!-- Direct link item -->
						<button
							v-if="item.link"
							@click="navigate(item.link)"
							class="flex items-center gap-3 w-full px-4 py-3 text-sm text-foreground hover:bg-muted transition-colors active:bg-muted/80">
							<component :is="item.icon" class="w-5 h-5 text-muted-foreground shrink-0" />
							{{ item.label }}
						</button>

						<!-- Group with children -->
						<div v-else-if="item.children">
							<button
								@click="toggleGroup(item.label)"
								class="flex items-center justify-between w-full px-4 py-3 text-sm text-foreground hover:bg-muted transition-colors active:bg-muted/80">
								<span class="flex items-center gap-3">
									<component :is="item.icon" class="w-5 h-5 text-muted-foreground shrink-0" />
									{{ item.label }}
								</span>
								<ChevronDown
									class="h-4 w-4 text-muted-foreground transition-transform duration-200"
									:class="{ 'rotate-180': expandedGroups.has(item.label) }" />
							</button>
							<div
								v-if="expandedGroups.has(item.label)"
								class="bg-muted/30">
								<button
									v-for="(child, childIndex) in item.children"
									:key="childIndex"
									@click="navigate(child.link)"
									class="flex items-center gap-3 w-full pl-12 pr-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors active:bg-muted/80">
									<component :is="child.icon" class="w-4 h-4 shrink-0" />
									{{ child.label }}
								</button>
							</div>
						</div>
					</template>
				</nav>
			</SheetContent>
		</Sheet>
	</div>
</template>
